import { useState, useEffect } from "react";
import BottomNav from "../components/BottomNav";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { objectsApi, transactionsApi } from "../services/api";

const T = {
  night:"#0A1F3D", royal:"#1263B0", cyan:"#1CBCD3", gold:"#C9922A",
  goldL:"#FBF4E8", cyanL:"#E0F7FA", greenL:"#F0FDF4", white:"#fff",
  bg:"#F7F9FC", border:"#E2E8F0", green:"#16A34A", red:"#EF4444"
};

const STATUS_COLOR = {
  EN_RECHERCHE:"#1CBCD3", ACTIF_RECHERCHE:"#1CBCD3",
  MATCHING_EN_COURS:"#C9922A", PREUVES_EN_COURS:"#C9922A",
  RESTITUTION_ACTIVE:"#1263B0", REMIS:"#16A34A",
  CLOTURE:"#64748B", NON_RECLAME:"#94A3B8", SUSPENDU:"#EF4444"
};

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [lostFiles, setLostFiles]     = useState([]);
  const [foundFiles, setFoundFiles]   = useState([]);
  const [matchings, setMatchings]     = useState([]);
  const [pendingAgreements, setPendingAgreements] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState("lost");

  useEffect(() => {
    Promise.all([
      objectsApi.getMyLost().catch(() => ({ data: { data: [] } })),
      objectsApi.getMyFound().catch(() => ({ data: { data: [] } })),
      objectsApi.getMyMatchings().catch(() => ({ data: { data: [] } })),
      transactionsApi.getPending().catch(() => ({ data: { data: [] } })),
    ]).then(([lostRes, foundRes, matchRes, pendingRes]) => {
      setLostFiles(lostRes.data?.data || []);
      setFoundFiles(foundRes.data?.data || []);
      setMatchings(matchRes.data?.data || []);
      setPendingAgreements(pendingRes.data?.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const handleLogout = () => { logout(); nav("/"); };
  const changeLang = (l) => {
    i18n.changeLanguage(l);
    localStorage.setItem("returnob_lang", l);
  };

  const totalFiles = lostFiles.length + foundFiles.length;
  const matchCount = matchings.length;

  return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"'DM Sans',sans-serif" }}>

      {/* HEADER */}
      <div style={{ background:`linear-gradient(135deg,${T.night},${T.royal})`,
        padding:"20px 24px", display:"flex", alignItems:"center",
        justifyContent:"space-between" }}>
        <Link to="/" style={{ textDecoration:"none" }}>
          <span style={{ fontFamily:"'Playfair Display',serif", color:T.white,
            fontSize:"20px", fontWeight:700 }}>ReturnOb</span>
        </Link>
        <div style={{ display:"flex", alignItems:"center", gap:"16px" }}>
          <div style={{ display:"flex", gap:"4px" }}>
            {["FR","EN"].map(l => (
              <button key={l} onClick={() => changeLang(l.toLowerCase())}
                style={{ fontSize:"11px", padding:"2px 8px", borderRadius:"5px",
                  cursor:"pointer",
                  background: lang===l.toLowerCase()
                    ? "rgba(255,255,255,0.2)" : "transparent",
                  color: lang===l.toLowerCase()
                    ? T.white : "rgba(255,255,255,0.4)",
                  border: lang===l.toLowerCase()
                    ? "1px solid rgba(255,255,255,0.3)" : "none",
                  fontWeight: lang===l.toLowerCase() ? 700 : 400 }}>
                {l}
              </button>
            ))}
          </div>
          <span style={{ color:"rgba(255,255,255,0.8)", fontSize:"14px" }}>
            {t("dashboard.greeting")}{" "}
            <strong>{user?.firstName || user?.first_name || "..."}</strong>
          </span>
          <button onClick={handleLogout}
            style={{ background:"rgba(255,255,255,0.1)", color:T.white,
              border:"1px solid rgba(255,255,255,0.2)",
              padding:"6px 14px", borderRadius:"8px",
              cursor:"pointer", fontSize:"13px" }}>
            {t("dashboard.logout")}
          </button>
        </div>
      </div>

      <div style={{ maxWidth:"900px", margin:"0 auto", padding:"32px 24px" }}>

        {/* NOTIFICATION MATCHING */}
        {matchCount > 0 && (
          <div style={{ background:`linear-gradient(135deg,${T.gold},#E6A020)`,
            borderRadius:"16px", padding:"16px 20px", marginBottom:"24px",
            display:"flex", alignItems:"center", gap:"14px",
            boxShadow:"0 4px 20px rgba(201,146,42,0.3)" }}>
            <div style={{ fontSize:"28px" }}>🎯</div>
            <div>
              <div style={{ color:T.white, fontWeight:700, fontSize:"15px" }}>
                {t("dashboard.match_found", { count: matchCount })}
              </div>
              <div style={{ color:"rgba(255,255,255,0.85)", fontSize:"13px" }}>
                {t("dashboard.match_action")}
              </div>
            </div>
            <Link to="/matchings" style={{ marginLeft:"auto", textDecoration:"none" }}>
              <button style={{ background:T.white, color:T.gold, border:"none",
                padding:"8px 16px", borderRadius:"8px",
                cursor:"pointer", fontWeight:700, fontSize:"13px" }}>
                {t("dashboard.view_matches")}
              </button>
            </Link>
          </div>
        )}

        {/* ACCORDS EN ATTENTE */}
        {pendingAgreements.length > 0 && (
          <div style={{ background:T.white, borderRadius:"16px",
            border:`1px solid ${T.border}`, padding:"20px", marginBottom:"24px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"14px" }}>
              <div style={{ width:"36px", height:"36px", borderRadius:"10px",
                background:T.cyanL, display:"flex", alignItems:"center",
                justifyContent:"center", fontSize:"18px" }}>📜</div>
              <div style={{ fontWeight:700, fontSize:"15px", color:T.night }}>
                {t("dashboard.pending_agreements_title", { count: pendingAgreements.length })}
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
              {pendingAgreements.map(pa => (
                <Link key={pa.reference} to={`/accords/${pa.reference}`} style={{ textDecoration:"none" }}>
                  <div style={{ padding:"14px 16px", borderRadius:"12px",
                    border:`1px solid ${T.border}`, display:"flex", alignItems:"center",
                    justifyContent:"space-between", cursor:"pointer",
                    transition:"background 0.2s" }}
                    onMouseEnter={e => e.currentTarget.style.background=T.bg}
                    onMouseLeave={e => e.currentTarget.style.background=T.white}>
                    <div>
                      <div style={{ fontWeight:600, fontSize:"13px", color:T.night,
                        marginBottom:"2px" }}>
                        {t(`transaction_types.${pa.transactionType}`,
                          { defaultValue: pa.transactionType })}
                      </div>
                      <div style={{ fontSize:"12px", color:"#64748B" }}>
                        {t("dashboard.pending_from", {
                          name: [pa.partie1?.firstName, pa.partie1?.lastName]
                            .filter(Boolean).join(" ") })}
                      </div>
                    </div>
                    <span style={{ fontSize:"11px", fontWeight:700, padding:"5px 12px",
                      borderRadius:"20px", background:T.cyanL, color:T.cyan,
                      flexShrink:0, marginLeft:"12px" }}>
                      {t("dashboard.pending_respond")}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* STATS */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)",
          gap:"16px", marginBottom:"28px" }}>
          {[
            { icon:"📁", value: totalFiles,
              label: t("dashboard.active_files"), color: T.royal, bg: T.cyanL },
            { icon:"📋", value: lostFiles.length,
              label: t("dashboard.lost_count"), color: T.royal, bg: T.cyanL },
            { icon:"🎒", value: foundFiles.length,
              label: t("dashboard.found_count"), color: T.gold, bg: T.goldL },
          ].map((s, i) => (
            <div key={i} style={{ background:T.white, borderRadius:"16px",
              padding:"16px 20px", border:`1px solid ${T.border}`,
              display:"flex", alignItems:"center", gap:"12px" }}>
              <div style={{ width:"44px", height:"44px", background:s.bg,
                borderRadius:"12px", display:"flex", alignItems:"center",
                justifyContent:"center", fontSize:"20px" }}>{s.icon}</div>
              <div>
                <div style={{ fontSize:"26px", fontWeight:800, color:T.night }}>
                  {s.value}
                </div>
                <div style={{ fontSize:"12px", color:"#64748B" }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ACTIONS */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)",
          gap:"16px", marginBottom:"32px" }}>
          {[
            { to:"/declarer-perte", icon:"📋",
              title: t("dashboard.declare_loss"),
              desc: t("dashboard.declare_loss_desc"),
              bg: T.royal, color: T.white },
            { to:"/declarer-trouvaille", icon:"🎒",
              title: t("dashboard.declare_found"),
              desc: t("dashboard.declare_found_desc"),
              bg: T.gold, color: T.white },
            { to:"/certifier", icon:"📜",
              title: t("dashboard.certify"),
              desc: t("dashboard.certify_desc"),
              bg: T.cyan, color: T.white },
          ].map((item, i) => (
            <Link key={i} to={item.to} style={{ textDecoration:"none" }}>
              <div style={{ background:item.bg, borderRadius:"16px",
                padding:"20px", cursor:"pointer",
                transition:"transform 0.2s",
                boxShadow:`0 4px 15px ${item.bg}40` }}
                onMouseEnter={e => e.currentTarget.style.transform="translateY(-3px)"}
                onMouseLeave={e => e.currentTarget.style.transform="translateY(0)"}>
                <div style={{ fontSize:"28px", marginBottom:"10px" }}>{item.icon}</div>
                <div style={{ color:item.color, fontWeight:700, fontSize:"14px",
                  marginBottom:"4px" }}>{item.title}</div>
                <div style={{ color:`${item.color}CC`, fontSize:"12px" }}>
                  {item.desc}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* DOSSIERS */}
        <div style={{ background:T.white, borderRadius:"16px",
          padding:"24px", border:`1px solid ${T.border}` }}>

          {/* ONGLETS */}
          <div style={{ display:"flex", gap:"8px", marginBottom:"20px" }}>
            {[
              { key:"lost",  label:`${t("dashboard.losses")} (${lostFiles.length})` },
              { key:"found", label:`${t("dashboard.founds")} (${foundFiles.length})` },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                style={{ padding:"8px 16px", borderRadius:"8px",
                  cursor:"pointer", fontSize:"13px", fontWeight:600,
                  border:"none",
                  background: activeTab===tab.key ? T.night : T.bg,
                  color: activeTab===tab.key ? T.white : "#64748B" }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* LISTE */}
          {loading ? (
            <div style={{ textAlign:"center", padding:"40px", color:"#64748B" }}>
              {t("common.loading")}
            </div>
          ) : activeTab === "lost" ? (
            lostFiles.length === 0 ? (
              <EmptyState t={t} type="lost" />
            ) : (
              <FileList files={lostFiles} type="lost" t={t} />
            )
          ) : (
            foundFiles.length === 0 ? (
              <EmptyState t={t} type="found" />
            ) : (
              <FileList files={foundFiles} type="found" t={t} />
            )
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
}

function EmptyState({ t, type }) {
  return (
    <div style={{ textAlign:"center", padding:"40px" }}>
      <div style={{ fontSize:"48px", marginBottom:"16px" }}>
        {type === "lost" ? "📬" : "🎒"}
      </div>
      <p style={{ color:"#64748B", fontSize:"14px" }}>
        {type === "lost"
          ? t("dashboard.no_losses")
          : t("dashboard.no_founds")}
      </p>
    </div>
  );
}

function FileList({ files, type, t }) {
  const T = {
    night:"#0A1F3D", royal:"#1263B0", cyan:"#1CBCD3", gold:"#C9922A",
    white:"#fff", bg:"#F7F9FC", border:"#E2E8F0"
  };
  const STATUS_COLOR = {
    EN_RECHERCHE:"#1CBCD3", ACTIF_RECHERCHE:"#1CBCD3",
    MATCHING_EN_COURS:"#C9922A", PREUVES_EN_COURS:"#C9922A",
    RESTITUTION_ACTIVE:"#1263B0", REMIS:"#16A34A",
    CLOTURE:"#64748B", NON_RECLAME:"#94A3B8", SUSPENDU:"#EF4444"
  };
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>
      {files.map((f, i) => (
        <Link key={i}
          to={type === "lost" ? `/dossiers/${f.id}` : `/trouvailles/${f.id}`}
          style={{ textDecoration:"none" }}>
          <div style={{ padding:"16px", borderRadius:"12px",
            border:`1px solid ${T.border}`,
            display:"flex", alignItems:"center",
            justifyContent:"space-between", cursor:"pointer",
            transition:"background 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.background=T.bg}
            onMouseLeave={e => e.currentTarget.style.background=T.white}>
            <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
              <div style={{ width:"36px", height:"36px", borderRadius:"10px",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:"16px",
                background: type === "lost" ? "#EFF6FF" : "#FBF4E8" }}>
                {type === "lost" ? "📋" : "🎒"}
              </div>
              <div>
                <div style={{ fontWeight:600, fontSize:"14px", color:T.night,
                  marginBottom:"2px" }}>
                  {f.objectName || f.object_name || f.name}
                </div>
                <div style={{ fontSize:"12px", color:"#64748B" }}>
                  {f.reference}
                </div>
              </div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
              {type === "lost" && (
                <span style={{ fontSize:"10px", fontWeight:700,
                  padding:"2px 8px", borderRadius:"20px",
                  background:"#EFF6FF", color:T.royal }}>
                  {t("dashboard.loss_badge")}
                </span>
              )}
              {type === "found" && (
                <span style={{ fontSize:"10px", fontWeight:700,
                  padding:"2px 8px", borderRadius:"20px",
                  background:"#FBF4E8", color:T.gold }}>
                  {t("dashboard.found_badge")}
                </span>
              )}
              <span style={{ fontSize:"11px", fontWeight:700,
                padding:"4px 10px", borderRadius:"20px",
                background:`${STATUS_COLOR[f.status] || "#94A3B8"}20`,
                color: STATUS_COLOR[f.status] || "#94A3B8" }}>
                {f.status}
              </span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}
